import React, { useMemo } from 'react';

import moment from 'moment';

import Carousel from 'components/Carousel';
import withGrid from 'hocs/withGrid';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { HotelsResultResponse } from 'models/hotelsSearch';
import { getFormattedPrice } from 'utils/currency';

import HotelStars from '../../HotelsStars';
import {
  Card,
  CardHeader,
  CardContent,
  CardRow,
  HotelApprovalStatus,
  HotelTitle,
  HotelSubtitle,
  CardFooterPrice,
  CardFooterFullPrice,
  CardFooter,
} from './styles';

interface Props {
  data: HotelsResultResponse;
  onClick?: () => void;
  testId?: string;
}

const CardHotel = ({ data, onClick, testId }: Props) => {
  const { t } = useStoreTranslation();
  const formattedPrice = (value: number | undefined) => {
    const { formatted } = getFormattedPrice({ value, currency: data.currencyCode, t });
    return formatted;
  };
  const total = data.priceFrom;

  const hasBreakRulesMessage = !!data.breakRulesMessage && data.breakRulesMessage.length > 0;

  const amountDays = useMemo(() => {
    const iDate = moment(data.checkinDate, 'YYYY-MM-DD');
    const eDate = moment(data.checkoutDate, 'YYYY-MM-DD');
    return eDate.diff(iDate, 'days');
  }, [data.checkinDate, data.checkoutDate]);

  const approvalMessage = useMemo(() => {
    if (hasBreakRulesMessage) return data.breakRulesMessage ? data.breakRulesMessage[0] : '';
    if (data.isPreApproved) return t('hotelsCard.preApproved');
    return t('hotelsCard.withinPolicies');
  }, [data, hasBreakRulesMessage, t]);

  const perNightPrice = formattedPrice(total / amountDays);

  return (
    <Card data-testid={testId}>
      <CardHeader>
        <Carousel fit="cover" images={data.images} enableKeyboard={false} />
      </CardHeader>
      <CardContent onClick={onClick}>
        <CardRow>
          <HotelTitle>{data.hotelName}</HotelTitle>
          <HotelStars count={5} value={data.category} />
        </CardRow>
        <CardRow>
          <HotelSubtitle>{data.city}</HotelSubtitle>
          <HotelApprovalStatus isError={hasBreakRulesMessage}>{approvalMessage}</HotelApprovalStatus>
        </CardRow>
      </CardContent>
      <CardFooter>
        <CardFooterPrice>{t('hotelsCard.nightPrice', { value: perNightPrice })}</CardFooterPrice>
        <CardFooterFullPrice>{formattedPrice(total)}</CardFooterFullPrice>
      </CardFooter>
    </Card>
  );
};

export default withGrid(CardHotel);
